/** author：胡枭 */

// 后端api
import { checkApi, getEmployeeInfoApi, logoutApi } from '@/api/user.js'
import {
  getVipGuaranteeApi,
  queryValueAddedServiceAccountBalanceApi,
  queryValueAddedServiceCategoryRightsApi,
  queryValueAddedServiceProductRightsApi
} from '@/api/value-added-service'
import {
  valueAddedCategoryCodeDict,
  valueAddedEffectTypeDict
} from '@/dictionary/value-added-service'

import { isEmployeeHasESignRightApi } from '@/api/signature.js'

const state = {
  // 用户信息
  userId: '', // 用户ID
  nickname: '', // 用户昵称，监控上报信息时需要使用
  face: '', // 头像
  isBindWX: false, // 是否绑定微信

  // 员工信息
  roles: [], // 角色权限
  verifyRoles: '', // 用于国密角色校验
  verifyIsAdmin: '', // 用于国密角色校验
  funRights: [], // 功能权限
  isAdmin: false, // false 非管理员，true 管理员
  mobile: '', // 手机号
  employeeEmail: '', // 员工邮箱
  employeeName: '', // 员工姓名
  isDefaultPassword: '', // 是否为默认密码 0: 否 1: 是
  deleted: '', // 员工的是否被删除 0：未删除 1：已删除
  employeeId: '', // 员工id
  employeeStatus: '', // 员工状态 0：禁用 1：启用
  deptId: '', // 员工部门id
  deptName: '', // 员工部门信息
  isSigner: false, // 是否拥有签章权限

  loginRoles: [], // 当前登录的角色
  hasLoginCompany: false,
  hasLoginExpert: false,

  // 用户会员信息
  vipInfo: {},
  // 用户会员权益余额
  vipBalance: {},
  // 用户会员保证金信息
  vipGuarantee: {}
}

const getters = {
  // 会员有效中
  inVip: state => state.vipInfo?.rightsId && !state.vipInfo?.isExpire,
  // 会员类型编码
  vipCode: state => state.vipInfo?.productInfo?.productCode
}

const mutations = {
  SET_USER_VERIFY_ROLES: (state, verifyRoles) => {
    state.verifyRoles = verifyRoles
  },
  SET_USER_VERIFY_IS_ADMIN: (state, verifyIsAdmin) => {
    state.verifyIsAdmin = verifyIsAdmin
  },
  SET_USER_ID: (state, id) => {
    state.userId = id
  },
  SET_USER_NICK_NAME: (state, nickname) => {
    state.nickname = nickname
  },
  SET_USER_FACE: (state, face) => {
    state.face = face
  },
  SET_USER_BIND_WX: (state, isBindWX) => {
    state.isBindWX = isBindWX
  },

  SET_IS_ADMIN: (state, isAdmin) => {
    state.isAdmin = isAdmin
  },
  SET_USER_ROLES: (state, roles) => {
    state.roles = roles
  },
  SET_USER_FUN_RIGHTS: (state, data) => {
    state.funRights = data?.employeeInfo?.features || []
  },
  SET_EMPLOYEE_SIGNER: (state, signer) => {
    state.isSigner = signer
  },
  SET_USER_MOBILE: (state, mobile) => {
    state.mobile = mobile
  },
  SET_EMPLOYEE_NAME: (state, name) => {
    state.employeeName = name
  },
  SET_EMPLOYEE_EMAIL: (state, email) => {
    state.employeeEmail = email
  },
  SET_USER_IS_DEFAULT_PASSWORD: (state, isDefaultPassword) => {
    state.isDefaultPassword = isDefaultPassword
  },
  SET_USER_DELETED: (state, deleted) => {
    state.deleted = deleted
  },
  SET_EMPLOYEE_ID: (state, employeeId) => {
    state.employeeId = employeeId
  },
  SET_EMPLOYEE_STATUS: (state, employeeStatus) => {
    state.employeeStatus = employeeStatus
  },
  SET_EMPLOYEE_DEPT_ID: (state, deptId) => {
    state.deptId = deptId
  },
  SET_EMPLOYEE_DEPT_NAME: (state, deptName) => {
    state.deptName = deptName
  },

  // 存储当前登录的角色
  SET_LOGIN_ROLES: (state, loginRoles) => {
    state.loginRoles = loginRoles
  },

  SET_HAS_LOGIN_COMPANY: (state, isLogin) => {
    state.hasLoginCompany = isLogin
  },

  SET_HAS_LOGIN_EXPERT: (state, isLogin) => {
    state.hasLoginExpert = isLogin
  },

  SET_VIP_INFO: (state, vipInfo) => {
    state.vipInfo = vipInfo
  },

  // 添加用户会员权益余额信息
  ADD_VIP_BALANCE: (state, vipBalance) => {
    state.vipBalance = Object.assign({}, state.vipBalance, vipBalance)
  },
  SET_VIP_BALANCE: (state, vipBalance) => {
    state.vipBalance = vipBalance
  },

  SET_VIP_GUARANTEE: (state, guarantee) => {
    state.vipGuarantee = guarantee
  }
}

const actions = {
  // 检验用户的登录状态
  checkLoginStatus({ commit, status }) {
    return new Promise((resolve, reject) => {
      checkApi()
        .then(res => {
          const _data = res?.data || {}
          const loginRoles = []
          if (_data.purchase) {
            loginRoles.push('purchase')
            commit('SET_HAS_LOGIN_COMPANY', true)
          }
          if (_data.expert) {
            loginRoles.push('expert')
            commit('SET_HAS_LOGIN_EXPERT', true)
          }
          commit('SET_LOGIN_ROLES', loginRoles)
          resolve(_data)
        })
        .catch(err => {
          commit('SET_HAS_LOGIN_COMPANY', false)
          commit('SET_HAS_LOGIN_EXPERT', false)
          commit('SET_LOGIN_ROLES', [])
          reject(err)
        })
    })
  },

  // 获取用户（员工）信息
  getEmployeeInfo({ commit, state }, needLoading = true) {
    return new Promise((resolve, reject) => {
      getEmployeeInfoApi(needLoading)
        .then(res => {
          const data = res?.data || {}

          commit('SET_USER_ID', data.userId || '')
          commit('SET_USER_FACE', data.userInfo?.face || '')
          commit('SET_USER_NICK_NAME', data?.userInfo?.nickname || '')
          commit('SET_USER_MOBILE', data.userInfo?.mobile || '')
          commit('SET_USER_VERIFY_ROLES', data.employeeInfo?.role || '')
          commit('SET_USER_VERIFY_IS_ADMIN', data.isAdmin || 0)
          commit(
            'SET_USER_IS_DEFAULT_PASSWORD',
            data.userInfo?.isDefaultPassword || ''
          )
          commit('SET_USER_BIND_WX', !!data.userInfo?.wxMpOpenid)

          commit('SET_USER_FUN_RIGHTS', data)
          commit('SET_EMPLOYEE_EMAIL', data.employeeInfo?.email || '')
          commit('SET_EMPLOYEE_NAME', data.employeeInfo?.name || '')
          commit('SET_USER_DELETED', data.deleted || '')
          commit('SET_EMPLOYEE_ID', data.employeeId || '')
          commit('SET_EMPLOYEE_STATUS', data.status || '')
          commit('SET_EMPLOYEE_DEPT_ID', data.deptInfo?.deptId || '')
          commit('SET_EMPLOYEE_DEPT_NAME', data.deptInfo?.name || '')
          commit('SET_IS_ADMIN', Boolean(data.isAdmin || ''))

          resolve(data)
        })
        .catch(err => {
          reject(err)
        })
    })
  },

  // 获取用户是否有在线签章的权限
  isEmployeeHasESignRight({ commit }) {
    return new Promise((resolve, reject) => {
      isEmployeeHasESignRightApi()
        .then(res => {
          commit('SET_EMPLOYEE_SIGNER', res.data)
          resolve(res.data)
        })
        .catch(err => {
          reject(err)
        })
    })
  },

  // 退出登录
  logout({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      logoutApi()
        .then(res => {
          dispatch('resetUser').then(res2 => {
            resolve(res)
          })
        })
        .catch(err => {
          reject(err)
        })
    })
  },

  // 重置用户
  resetUser({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      commit('SET_USER_ROLES', [])
      commit('SET_HAS_LOGIN_COMPANY', false)
      commit('SET_HAS_LOGIN_EXPERT', false)
      commit('SET_LOGIN_ROLES', [])
      commit('SET_VIP_INFO', {})
      commit('SET_VIP_BALANCE', {})
      resolve()
    })
  },

  // 查询用户会员权益信息
  getVipInfo({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      queryValueAddedServiceCategoryRightsApi(
        valueAddedCategoryCodeDict.VIP.value
      )
        .then(res => {
          const vipInfoList = res.data || []
          if (vipInfoList.length === 0) {
            commit('SET_VIP_INFO', {})
            resolve({})
            return
          }

          // 有会员权益的情况下，取第一个会员权益
          const vipInfo = vipInfoList.find(item => !item.isExpire) || {}
          commit('SET_VIP_INFO', vipInfo)
          resolve(vipInfo)

          dispatch('getVipGuaranteeInfo', vipInfo.productInfo.productCode)
        })
        .catch(err => reject(err))
    })
  },

  // 查询用户会员权益余额信息
  getVipBalance({ commit }, productList) {
    return new Promise((resolve, reject) => {
      const promiseList = productList.map(item => {
        if (item.effectType === valueAddedEffectTypeDict.TIME.value) {
          return queryValueAddedServiceProductRightsApi(item.value)
        }
        if (item.effectType === valueAddedEffectTypeDict.COUNT.value) {
          return queryValueAddedServiceAccountBalanceApi(item.value)
        }
      })
      Promise.allSettled(promiseList)
        .then(res => {
          const balance = {}
          const list = res || []
          list.forEach((item, index) => {
            const productCode = productList[index].value
            balance[productCode] = item.value?.data || {}
          })
          commit('ADD_VIP_BALANCE', balance)
          resolve(res)
        })
        .catch(err => reject(err))
    })
  },

  // 查询会员保证金额度信息
  getVipGuaranteeInfo({ commit }, productCode) {
    return new Promise((resolve, reject) => {
      getVipGuaranteeApi({ productCode })
        .then(res => {
          commit('SET_VIP_GUARANTEE', {
            total: res.data.totalQuota || 0,
            remain: res.data.remainQuota || 0
          })
          resolve(res)
        })
        .catch(err => {
          commit('SET_VIP_GUARANTEE', {
            total: 0,
            remain: 0
          })
          reject(err)
        })
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
